import { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Edit, Trash2, Save } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { supabase, QueryTemplate } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canManageTemplate, canAddToLibrary } from "./queryLibraryAccess.js";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from "@/components/ui/sheet";

import { AI_MODEL_OPTIONS } from "../tabs/ai-workflows/aiModelSettings";
const ANY_MODEL = "__any__";
const modelLabel = (v?: string | null) => AI_MODEL_OPTIONS.find(o => o.value === v)?.label || v || "";

interface FormData {
  name: string;
  description: string;
  query: string;
  category: string;
  ai_model: string;
  data_tag_1: string;
  data_tag_2: string;
  data_tag_3: string;
  data_tag_4: string;
  data_tag_5: string;
}

interface AIQueryLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectQuery: (query: string, dataTags?: string[], model?: string | null) => void;
  aiWorkflow?: string;
  /** Organisation that owns the current camera (superuser "organisation" option). */
  organizationId?: string | null;
  /** Change `nonce` to open the add form pre-filled with the camera's current query. */
  addRequest?: { nonce: number; query: string; dataTags: string[]; workflow?: string; model?: string | null } | null;
}

export const AIQueryLibrary: React.FC<AIQueryLibraryProps> = ({ 
  isOpen, 
  onClose, 
  onSelectQuery,
  aiWorkflow,
  organizationId,
  addRequest
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTemplates, setFilteredTemplates] = useState<QueryTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<QueryTemplate | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  
  const form = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      query: "",
      category: "",
      ai_model: "",
      data_tag_1: "",
      data_tag_2: "",
      data_tag_3: "",
      data_tag_4: "",
      data_tag_5: "",
    }
  });
  
  const { session } = useAuth();
  const { data: userInfo } = useQuery({
    queryKey: ["query-library-user", session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role, organization_id')
        .eq('user_id', session!.user.id);
      const rows = (data || []) as { role: string; organization_id: string | null }[];
      const su = rows.find(r => r.role === 'superuser');
      const admin = rows.find(r => r.role === 'admin' && r.organization_id);
      const pick = su || admin || rows[0];
      return { role: pick?.role || 'standard', organizationId: pick?.organization_id || null };
    }
  });
  const user = userInfo || { role: 'standard', organizationId: null };
  const isSuperuser = user.role === 'superuser';
  const canAdd = canAddToLibrary(user);
  const [scope, setScope] = useState<"universal" | "organisation">("universal");
  const [cameraOrgName, setCameraOrgName] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) { setCameraOrgName(null); return; }
    supabase.from('organizations').select('name').eq('id', organizationId).maybeSingle()
      .then(({ data }) => setCameraOrgName((data as any)?.name ?? null));
  }, [organizationId]);

  useEffect(() => {
    if (!addRequest) return;
    const t = addRequest.dataTags || [];
    form.reset({
      name: "", description: "", query: addRequest.query, category: "",
      ai_model: addRequest.model || "",
      data_tag_1: t[0] || "", data_tag_2: t[1] || "", data_tag_3: t[2] || "",
      data_tag_4: t[3] || "", data_tag_5: t[4] || "",
    });
    setScope(isSuperuser ? "universal" : "organisation");
    setIsAddingNew(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequest?.nonce]);

  const { data: queryTemplates, isLoading, refetch } = useQuery({
    queryKey: ["ai-query-templates", aiWorkflow],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('ai_query_templates')
          .select('*')
          .order('name');
        
        if (error) {
          toast.error("Error loading query templates");
          console.error("Error fetching query templates:", error);
          return [];
        }
        
        return data as QueryTemplate[];
      } catch (err) {
        console.error("Exception fetching templates:", err);
        return [];
      }
    }
  });
  
  // Filter templates based on search term AND ai_workflow
  useEffect(() => {
    if (queryTemplates) {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = queryTemplates.filter(template => {
        // Search filter
        return (
          template.name.toLowerCase().includes(lowercasedSearch) || 
          template.description.toLowerCase().includes(lowercasedSearch) || 
          template.query.toLowerCase().includes(lowercasedSearch) ||
          template.category.toLowerCase().includes(lowercasedSearch)
        );
      });
      setFilteredTemplates(filtered);
    }
  }, [searchTerm, queryTemplates, aiWorkflow]);
  
  const handleSelectQuery = () => {
    if (selectedTemplate) {
      onSelectQuery(selectedTemplate.query, selectedTemplate.data_tags || undefined, selectedTemplate.ai_model || null);
      onClose();
    }
  };

  const handleTemplateClick = (template: QueryTemplate) => {
    setSelectedTemplate(template);
  };
  
  const handleEditClick = (template: QueryTemplate) => {
    form.reset({
      name: template.name,
      description: template.description,
      query: template.query,
      category: template.category,
      ai_model: template.ai_model || "",
      data_tag_1: (template.data_tags as string[] || [])[0] || "",
      data_tag_2: (template.data_tags as string[] || [])[1] || "",
      data_tag_3: (template.data_tags as string[] || [])[2] || "",
      data_tag_4: (template.data_tags as string[] || [])[3] || "",
      data_tag_5: (template.data_tags as string[] || [])[4] || "",
    });
    setSelectedTemplate(template);
    setIsEditing(true);
  };
  
  const handleCreateNew = () => {
    form.reset({
      name: "",
      description: "",
      query: "",
      category: "",
      ai_model: "",
      data_tag_1: "",
      data_tag_2: "",
      data_tag_3: "",
      data_tag_4: "",
      data_tag_5: "",
    });
    setScope(isSuperuser ? "universal" : "organisation");
    setIsAddingNew(true);
  };
  
  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
    setIsDeleting(true);
  };
  
  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('ai_query_templates')
        .delete()
        .eq('id', deleteId);
        
      if (error) throw error;
      
      toast.success("Query template deleted");
      refetch();
      setIsDeleting(false);
      
      if (selectedTemplate?.id === deleteId) {
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };
  
  const onSubmit = async (data: FormData) => {
    try {
      const dataTags = [data.data_tag_1, data.data_tag_2, data.data_tag_3, data.data_tag_4, data.data_tag_5]
        .map(t => t.trim())
        .filter(Boolean);

      const payload = {
        name: data.name,
        description: data.description,
        query: data.query,
        category: data.category,
        ai_model: data.ai_model || null,
        data_tags: dataTags
      };

      if (isAddingNew) {
        const ownerOrg = isSuperuser
          ? (scope === "organisation" ? organizationId || null : null)
          : user.organizationId;
        if (!isSuperuser && !ownerOrg) throw new Error("No organisation");
        const { error } = await supabase
          .from('ai_query_templates')
          .insert({ ...payload, organization_id: ownerOrg } as any);
          
        if (error) throw error;
        toast.success("New query template created");
      } else {
        const { error } = await supabase
          .from('ai_query_templates')
          .update(payload)
          .eq('id', selectedTemplate?.id);
          
        if (error) throw error;
        toast.success("Query template updated");
      }
      
      refetch();
      setIsEditing(false);
      setIsAddingNew(false);
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">AI Image Query Library</DialogTitle>
          </DialogHeader>
          
          <div className="flex justify-between items-center mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by name, description or query content..."
                className="pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            
            {canAdd && (
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-2 whitespace-nowrap"
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? "View Mode" : "Edit Mode"}
              </Button>
            )}
            
            {canAdd && editMode && (
              <Button 
                size="sm"
                className="ml-2 text-black"
                style={{ backgroundColor: '#DFEF97' }}
                onClick={handleCreateNew}
              >
                <Plus className="h-4 w-4 mr-1" /> Add New
              </Button>
            )}
          </div>

          
          <ScrollArea className="h-[350px] pr-3">
            <div className="grid grid-cols-1 gap-3">
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading queries...
                </div>
              ) : filteredTemplates.length > 0 ? (
                filteredTemplates.map(template => (
                  <Card 
                    key={template.id} 
                    data-selected={selectedTemplate?.id === template.id ? "true" : undefined}
                    className={`ai-query-row cursor-pointer transition-colors hover:bg-accent ${selectedTemplate?.id === template.id ? 'border-primary' : ''}`}
                    onClick={() => handleTemplateClick(template)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-base">{template.name}</h3>
                          <p className="text-sm text-muted-foreground">{template.description}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge variant={template.organization_id ? "default" : "outline"} className="text-[10px]">
                            {template.organization_id ? "Organisation" : "Universal"}
                          </Badge>
                          {template.ai_model && (
                            <Badge variant="secondary" className="text-[10px]">{modelLabel(template.ai_model)}</Badge>
                          )}
                          <span className="text-xs bg-secondary text-secondary-foreground rounded-full px-2 py-1">
                            {template.category}
                          </span>
                          
                          {editMode && canManageTemplate(user, template) && (
                            <div className="flex ml-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(template);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(template.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{template.query}</p>
                      {template.data_tags && template.data_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.data_tags.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No queries found matching your search
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={handleSelectQuery}
              disabled={!selectedTemplate}
              className="text-black"
              style={{ backgroundColor: '#DFEF97' }}
            >
              Use Selected Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Template Sheet */}
      <Sheet open={isEditing || isAddingNew} onOpenChange={(open) => {
        if (!open) {
          setIsEditing(false);
          setIsAddingNew(false);
        }
      }}>
        <SheetContent className="sm:max-w-3xl w-full flex flex-col">
          <SheetHeader className="pb-6 border-b px-6">
            <SheetTitle className="text-xl font-semibold">
              {isAddingNew ? "Add New Query Template" : "Edit Query Template"}
            </SheetTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {isAddingNew 
                ? "Create a reusable AI query template for image analysis." 
                : "Update your query template settings and content."
              }
            </p>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto py-6 px-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {isAddingNew && (
                  <div className="space-y-2">
                    <FormLabel>Save to</FormLabel>
                    {isSuperuser ? (
                      <Select value={scope} onValueChange={(v) => setScope(v as any)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="universal">Universal library (all organisations)</SelectItem>
                          {organizationId && (
                            <SelectItem value="organisation">
                              {cameraOrgName ? `${cameraOrgName} library` : "This camera's organisation library"}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-muted-foreground">Your organisation's private library</p>
                    )}
                  </div>
                )}
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="ai-query-bar h-1 w-8 bg-[#DFEF97] rounded-full"></div>
                    <h3 className="text-sm font-medium text-foreground">Basic Information</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Template Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="e.g., Security Check, Wildlife Detection" 
                            className="h-11"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Category</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Security, Wildlife, Maintenance, etc." 
                            className="h-11"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ai_model"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">AI Model</FormLabel>
                        <Select onValueChange={v => field.onChange(v === ANY_MODEL ? "" : v)} value={field.value || ANY_MODEL}>
                          <FormControl>
                            <SelectTrigger className="h-11">
                              <SelectValue placeholder="Any model" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={ANY_MODEL}>Any model</SelectItem>
                            {AI_MODEL_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Description</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Brief description of what this query does" 
                            className="h-11"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Query Content Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="ai-query-bar h-1 w-8 bg-[#DFEF97] rounded-full"></div>
                    <h3 className="text-sm font-medium text-foreground">Query Content</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="query"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">AI Query Text</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Enter your AI image analysis query here. Be specific about what you want the AI to look for in the images..."
                            className="min-h-[240px] resize-none"
                            {...field} 
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground mt-2">
                          Tip: Be specific about objects, behaviors, or conditions you want to detect.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <FormLabel className="text-sm font-medium">Data Tags</FormLabel>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">
                      Up to 5 data tags for structured AI output.
                    </p>
                    <div className="grid grid-cols-5 gap-3">
                      {([1, 2, 3, 4, 5] as const).map((num) => (
                        <FormField
                          key={num}
                          control={form.control}
                          name={`data_tag_${num}` as keyof FormData}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs text-muted-foreground">Tag {num}</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={`Tag ${num}`}
                                  className="h-11"
                                  {...field}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </form>
            </Form>
          </div>

          <SheetFooter className="pt-6 border-t flex gap-3">
            <Button 
              variant="outline" 
              type="button" 
              className="flex-1"
              onClick={() => {
                setIsEditing(false);
                setIsAddingNew(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 text-black"
              style={{ backgroundColor: '#DFEF97' }}
              onClick={form.handleSubmit(onSubmit)}
            >
              <Save className="h-4 w-4 mr-2" />
              {isAddingNew ? "Create Template" : "Update Template"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleting} onOpenChange={setIsDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the query template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
